import React, { useState, useEffect } from 'react';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Slider from '@mui/material/Slider';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DownloadIcon from '@mui/icons-material/Download';
import SaveIcon from '@mui/icons-material/Save';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import Grid from '@mui/material/Grid';
import { calculateScenario, saveScenario, generateCSVExport } from '../utils/projectionUtils';
import { generateProjectionPDF, downloadPDF } from '../utils/pdfUtils';

function ProjectionEstimator({ projectionId, baseData }) {
  const [percentCompleted, setPercentCompleted] = useState(100);
  const [percentRefused, setPercentRefused] = useState(0);
  const [scenarioName, setScenarioName] = useState('');
  const [isCalculating, setIsCalculating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isPdfExporting, setIsPdfExporting] = useState(false);
  const [currentScenario, setCurrentScenario] = useState(null);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [savedScenarioId, setSavedScenarioId] = useState(null);

  // Run initial calculation when component mounts
  useEffect(() => {
    calculateCurrentScenario();
  }, []);

  const calculateCurrentScenario = async () => {
    if (!projectionId) return;
    
    try {
      setIsCalculating(true);
      setError(null);
      
      const scenarioResults = await calculateScenario(
        projectionId, 
        percentCompleted, 
        percentRefused
      );
      
      setCurrentScenario(scenarioResults);
    } catch (error) {
      console.error('Error calculating scenario:', error);
      setError('Failed to calculate scenario: ' + error.message);
    } finally {
      setIsCalculating(false);
    }
  };

  const handleCompletedChange = (_, newValue) => {
    // Ensure percentCompleted + percentRefused <= 100
    const maxRefused = 100 - newValue;
    setPercentCompleted(newValue);
    
    if (percentRefused > maxRefused) {
      setPercentRefused(maxRefused);
    }
  };

  const handleRefusedChange = (_, newValue) => {
    // Get current values before setting new ones
    const previousRefused = percentRefused;
    
    // Set the new refused percentage
    setPercentRefused(newValue);
    
    // If refused percentage decreased, automatically increase completed percentage
    if (newValue < previousRefused && percentCompleted + newValue < 100) {
      // Calculate how much to increase completed percentage
      const increase = Math.min(previousRefused - newValue, 100 - (percentCompleted + newValue));
      setPercentCompleted(percentCompleted + increase);
    } else {
      // Original logic for when refused increases
      const maxCompleted = 100 - newValue;
      if (percentCompleted > maxCompleted) {
        setPercentCompleted(maxCompleted);
      }
    }
  };

  const handleSaveScenario = async () => {
    if (!projectionId || !scenarioName.trim()) {
      setError('Please provide a name for this scenario');
      return;
    }
    
    try {
      setIsSaving(true);
      setError(null);
      
      const result = await saveScenario(
        projectionId,
        scenarioName.trim(),
        percentCompleted,
        percentRefused
      );
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to save scenario');
      }
      
      setSavedScenarioId(result.scenario.id);
      setSuccessMessage(`Scenario "${scenarioName}" saved successfully`);
      setScenarioName('');
      
      // Show success message for 3 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
      
    } catch (error) {
      console.error('Error saving scenario:', error);
      setError('Failed to save scenario: ' + error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const exportCsv = async () => {
    if (!projectionId || !currentScenario) return;
    
    try {
      setIsExporting(true);
      setError(null);
      
      // Save a temporary scenario for the export if one doesn't exist
      let scenarioId;
      if (!savedScenarioId) {
        const name = `Temporary Export (${percentCompleted}% completed, ${percentRefused}% refused)`;
        const saveResult = await saveScenario(
          projectionId,
          name,
          percentCompleted,
          percentRefused
        );
        
        if (!saveResult.success) {
          throw new Error(saveResult.error || 'Failed to create temporary scenario for export');
        }
        
        scenarioId = saveResult.scenario.id;
      } else {
        scenarioId = savedScenarioId;
      }
      
      // Generate CSV content
      const csvContent = await generateCSVExport(projectionId, scenarioId);
      
      // Trigger download
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `projection_${projectionId}_scenario.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Error exporting CSV:', error);
      setError('Failed to export CSV: ' + error.message);
    } finally {
      setIsExporting(false);
    }
  };
  
  const exportPdf = async () => {
    if (!projectionId || !currentScenario) return;
    
    try {
      setIsPdfExporting(true);
      setError(null);
      
      // Save a temporary scenario for the export if one doesn't exist
      let scenarioId;
      if (!savedScenarioId) {
        const name = `Temporary Export (${percentCompleted}% completed, ${percentRefused}% refused)`;
        const saveResult = await saveScenario(
          projectionId,
          name,
          percentCompleted,
          percentRefused
        );
        
        if (!saveResult.success) {
          throw new Error(saveResult.error || 'Failed to create temporary scenario for export');
        }
        
        scenarioId = saveResult.scenario.id;
      } else {
        scenarioId = savedScenarioId;
      }
      
      // Generate PDF
      const pdfBlob = await generateProjectionPDF(projectionId, scenarioId);
      
      // Download the PDF
      downloadPDF(pdfBlob, `projection_${projectionId}_scenario.pdf`);
      
    } catch (error) {
      console.error('Error exporting PDF:', error);
      setError('Failed to export PDF: ' + error.message);
    } finally {
      setIsPdfExporting(false);
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return '0';
    return new Intl.NumberFormat('en-US').format(num);
  };

  const formatPercent = (num) => {
    if (num === null || num === undefined) return '0%';
    return new Intl.NumberFormat('en-US', {
      style: 'percent',
      minimumFractionDigits: 1,
      maximumFractionDigits: 1
    }).format(num / 100);
  };

  // Calculate delta values between base projection and current scenario
  const calculateDelta = (baseValue, currentValue) => {
    if (baseValue === undefined || currentValue === undefined) return 0;
    return currentValue - baseValue;
  };

  const renderDeltaValue = (baseValue, currentValue, isCurrency = true, isPercent = false) => {
    const delta = calculateDelta(baseValue, currentValue);
    let formattedDelta;
    
    if (isPercent) {
      formattedDelta = new Intl.NumberFormat('en-US', {
        style: 'percent',
        minimumFractionDigits: 1,
        maximumFractionDigits: 1
      }).format(delta / 100);
    } else if (isCurrency) {
      formattedDelta = formatCurrency(delta);
    } else {
      formattedDelta = formatNumber(delta);
    }
    
    const isPositive = delta > 0;
    const color = delta === 0 ? 'text.secondary' : (isPositive ? 'success.main' : 'error.main');
    
    return (
      <Typography variant="body2" color={color} sx={{ fontWeight: 'bold' }}>
        {delta === 0 ? 'No change' : `${isPositive ? '+' : ''}${formattedDelta}`}
      </Typography>
    );
  };

  // Calculate margin percentage
  const calculateMarginPercent = (margin, revenue) => {
    if (!revenue || revenue === 0) return 0;
    return (margin / revenue) * 100;
  };

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          Estimator Tool
        </Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          Adjust completion and refusal percentages to simulate different revenue outcomes.
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        {successMessage && (
          <Alert 
            severity="success" 
            sx={{ mb: 3 }}
            icon={<CheckCircleIcon fontSize="inherit" />}
          >
            {successMessage}
          </Alert>
        )}
        
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" gutterBottom>
              Completed Locations: {percentCompleted}%
            </Typography>
            <Slider
              value={percentCompleted}
              onChange={handleCompletedChange}
              aria-label="Completed Percentage"
              valueLabelDisplay="auto"
              step={5}
              marks
              min={0}
              max={100}
              sx={{ mb: 4 }}
            />
            
            <Typography variant="subtitle1" gutterBottom>
              Refused Locations: {percentRefused}%
            </Typography>
            <Slider
              value={percentRefused}
              onChange={handleRefusedChange}
              aria-label="Refused Percentage"
              valueLabelDisplay="auto"
              step={5}
              marks
              min={0}
              max={100}
              sx={{ mb: 4 }}
            />
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Note: The sum of completed and refused percentages cannot exceed 100%.
            </Typography>
            
            <Button
              variant="contained"
              onClick={calculateCurrentScenario}
              disabled={isCalculating}
              sx={{ mr: 2 }}
            >
              {isCalculating ? 'Calculating...' : 'Calculate'}
            </Button>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Scenario Results
                </Typography>
                
                {isCalculating ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <CircularProgress size={40} />
                  </Box>
                ) : currentScenario ? (
                  <Stack spacing={1}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body1">Total Locations:</Typography>
                      <Typography variant="body1">{formatNumber(currentScenario.totalLocations)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body1">Completed Locations:</Typography>
                      <Typography variant="body1">{formatNumber(currentScenario.completedLocations)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body1">Refused Locations:</Typography>
                      <Typography variant="body1">{formatNumber(currentScenario.refusedLocations)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body1">Total Units:</Typography>
                      <Typography variant="body1">{formatNumber(currentScenario.totalUnits || 0)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body1">Completion Revenue:</Typography>
                      <Typography variant="body1">{formatCurrency(currentScenario.completedRevenue)} ({formatNumber(currentScenario.completedLocations)} × $300)</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body1">Refusal Revenue:</Typography>
                      <Typography variant="body1">{formatCurrency(currentScenario.refusalRevenue)} ({formatNumber(currentScenario.refusedUnits || 0)} × $50)</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body1">Total Revenue:</Typography>
                      <Typography variant="body1">{formatCurrency(currentScenario.totalRevenue)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body1">Total Expense:</Typography>
                      <Typography variant="body1">{formatCurrency(currentScenario.totalExpense)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>Total Gross Profit:</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>{formatCurrency(currentScenario.totalMargin)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>Margin:</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                        {formatPercent(calculateMarginPercent(currentScenario.totalMargin, currentScenario.totalRevenue))}
                      </Typography>
                    </Box>
                  </Stack>
                ) : (
                  <Typography variant="body1">
                    Click "Calculate" to see results
                  </Typography>
                )}
              </CardContent>
            </Card>
            
            {baseData && currentScenario && (
              <Card variant="outlined" sx={{ mb: 3, bgcolor: 'background.default' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Compared to Base Projection
                  </Typography>
                  <Stack spacing={1}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body1">Revenue Change:</Typography>
                      {renderDeltaValue(baseData.total_projected_revenue, currentScenario.totalRevenue)}
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body1">Expense Change:</Typography>
                      {renderDeltaValue(baseData.total_projected_expense, currentScenario.totalExpense)}
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>Gross Profit Change:</Typography>
                      {renderDeltaValue(baseData.total_projected_margin, currentScenario.totalMargin)}
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body1" sx={{ fontWeight: 'bold' }}>Margin Change:</Typography>
                      {renderDeltaValue(
                        calculateMarginPercent(baseData.total_projected_margin, baseData.total_projected_revenue),
                        calculateMarginPercent(currentScenario.totalMargin, currentScenario.totalRevenue),
                        false,
                        true
                      )}
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            )}
          </Grid>
        </Grid>
        
        <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6}>
              <TextField
                label="Scenario Name"
                variant="outlined"
                fullWidth
                value={scenarioName}
                onChange={(e) => setScenarioName(e.target.value)}
                placeholder="e.g., Optimistic Q3 Forecast"
                disabled={isSaving}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveScenario}
                  disabled={!scenarioName.trim() || isSaving || !currentScenario}
                >
                  {isSaving ? 'Saving...' : 'Save Scenario'}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={exportCsv}
                  disabled={isExporting || !currentScenario}
                >
                  {isExporting ? 'Exporting...' : 'Export CSV'}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<PictureAsPdfIcon />}
                  onClick={exportPdf}
                  disabled={isPdfExporting || !currentScenario}
                >
                  {isPdfExporting ? 'Exporting...' : 'Export PDF'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Box>
  );
}

export default ProjectionEstimator; 